import { useMemo, useState, type ReactNode } from "react";
import {
  ArrowRight,
  BookOpen,
  Check,
  ChevronDown,
  ClipboardCheck,
  Copy,
  FileInput,
  Files,
  ListChecks,
  PlayCircle,
  Target,
} from "lucide-react";

import { Button } from "@/components/ui/button";

interface WorkflowInstructionPlaybookProps {
  workflowTitle: string;
  instructions: string;
  copied: boolean;
  onCopy: () => void;
}

interface StructuredInstruction {
  kind: "structured";
  intro: string;
  role: string;
  context: string;
  steps: string;
  input: string;
  output: string;
}

interface GuidedInstruction {
  kind: "guided";
  lead: string;
  body: string;
}

type ParsedInstruction = StructuredInstruction | GuidedInstruction;

interface MarkdownListItem {
  text: string;
  children: string[];
  checked?: boolean;
}

type MarkdownBlock =
  | { kind: "heading"; level: number; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "ordered-list"; items: MarkdownListItem[] }
  | { kind: "unordered-list"; items: MarkdownListItem[] }
  | { kind: "table"; headers: string[]; rows: string[][] }
  | { kind: "quote"; text: string }
  | { kind: "code"; text: string };

const STRUCTURED_HEADINGS = {
  role: /^## Role\s*$/,
  context: /^## Context Files\s*$/,
  steps: /^## Instructions\s*$/,
  input: /^## Input Format\s*$/,
  output: /^## Output Format(?:\s.*)?$/,
};

const findLine = (lines: string[], pattern: RegExp) =>
  lines.findIndex((line) => pattern.test(line.trim()));

const joinSection = (lines: string[], start: number, end?: number) =>
  lines
    .slice(start, end)
    .join("\n")
    .trim();

const splitLead = (text: string) => {
  const paragraphs = text.trim().split(/\n\s*\n/);
  if (paragraphs.length === 1) {
    return { lead: paragraphs[0], body: "" };
  }

  return {
    lead: paragraphs[0].trim(),
    body: paragraphs.slice(1).join("\n\n").trim(),
  };
};

const parseInstruction = (instructions: string): ParsedInstruction => {
  const lines = instructions.replace(/\r\n/g, "\n").trim().split("\n");
  const roleIndex = findLine(lines, STRUCTURED_HEADINGS.role);
  const contextIndex = findLine(lines, STRUCTURED_HEADINGS.context);
  const stepsIndex = findLine(lines, STRUCTURED_HEADINGS.steps);
  const inputIndex = findLine(lines, STRUCTURED_HEADINGS.input);
  const outputIndex = findLine(lines, STRUCTURED_HEADINGS.output);

  const isStructured =
    roleIndex >= 0 &&
    contextIndex > roleIndex &&
    stepsIndex > contextIndex &&
    inputIndex > stepsIndex &&
    outputIndex > inputIndex;

  if (isStructured) {
    const skillTitleIndex = lines.findIndex((line) =>
      /^# Skill:/.test(line.trim())
    );
    const introEnd =
      skillTitleIndex >= 0
        ? skillTitleIndex
        : lines.findIndex((line) => line.trim() === "---");

    return {
      kind: "structured",
      intro: joinSection(lines, 0, introEnd >= 0 ? introEnd : roleIndex).replace(
        /\n?---\s*$/,
        ""
      ),
      role: joinSection(lines, roleIndex + 1, contextIndex),
      context: joinSection(lines, contextIndex + 1, stepsIndex),
      steps: joinSection(lines, stepsIndex + 1, inputIndex),
      input: joinSection(lines, inputIndex + 1, outputIndex),
      output: joinSection(lines, outputIndex + 1),
    };
  }

  const { lead, body } = splitLead(instructions);
  return { kind: "guided", lead, body };
};

const isTableLine = (line: string) => {
  const trimmed = line.trim();
  return trimmed.startsWith("|") && trimmed.includes("|", 1);
};

const isTableDivider = (line: string) =>
  /^\s*\|?\s*:?-{3,}/.test(line) && line.includes("|");

const parseTableRow = (line: string) => {
  const trimmed = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  return trimmed.split("|").map((cell) => cell.trim());
};

const isLabelHeading = (line: string) => {
  const trimmed = line.trim();
  return (
    trimmed.length <= 64 &&
    /^[A-Z][^.!?]*:$/.test(trimmed) &&
    !trimmed.startsWith("http")
  );
};

const isBlockStart = (lines: string[], index: number) => {
  const line = lines[index] ?? "";
  const trimmed = line.trim();

  return (
    trimmed === "" ||
    /^#{1,6}\s+/.test(trimmed) ||
    /^>\s?/.test(trimmed) ||
    /^```/.test(trimmed) ||
    /^\d+\.\s+/.test(line) ||
    /^[-*]\s+/.test(line) ||
    isLabelHeading(line) ||
    (isTableLine(line) && isTableDivider(lines[index + 1] ?? ""))
  );
};

const parseMarkdownBlocks = (text: string): MarkdownBlock[] => {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks: MarkdownBlock[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed || /^---+$/.test(trimmed)) {
      index += 1;
      continue;
    }

    if (/^```/.test(trimmed)) {
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length && !/^```/.test(lines[index].trim())) {
        codeLines.push(lines[index]);
        index += 1;
      }
      index += 1;
      blocks.push({ kind: "code", text: codeLines.join("\n") });
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      blocks.push({
        kind: "heading",
        level: headingMatch[1].length,
        text: headingMatch[2],
      });
      index += 1;
      continue;
    }

    if (isLabelHeading(line)) {
      blocks.push({
        kind: "heading",
        level: 4,
        text: trimmed.replace(/:$/, ""),
      });
      index += 1;
      continue;
    }

    if (isTableLine(line) && isTableDivider(lines[index + 1] ?? "")) {
      const headers = parseTableRow(line);
      const rows: string[][] = [];
      index += 2;

      while (index < lines.length && isTableLine(lines[index])) {
        rows.push(parseTableRow(lines[index]));
        index += 1;
      }

      blocks.push({ kind: "table", headers, rows });
      continue;
    }

    if (/^>\s?/.test(trimmed)) {
      const quoteLines: string[] = [];
      while (index < lines.length && /^>\s?/.test(lines[index].trim())) {
        quoteLines.push(lines[index].trim().replace(/^>\s?/, ""));
        index += 1;
      }
      blocks.push({ kind: "quote", text: quoteLines.join(" ") });
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items: MarkdownListItem[] = [];
      let currentItem: MarkdownListItem | null = null;

      while (index < lines.length) {
        const currentLine = lines[index];
        const itemMatch = currentLine.match(/^(\d+)\.\s+(.+)$/);
        const childMatch = currentLine.match(/^\s+[-*]\s+(.+)$/);

        if (itemMatch) {
          currentItem = { text: itemMatch[2], children: [] };
          items.push(currentItem);
          index += 1;
          continue;
        }

        if (childMatch && currentItem) {
          currentItem.children.push(childMatch[1]);
          index += 1;
          continue;
        }

        break;
      }

      blocks.push({ kind: "ordered-list", items });
      continue;
    }

    if (/^[-*]\s+/.test(line)) {
      const items: MarkdownListItem[] = [];
      let currentItem: MarkdownListItem | null = null;

      while (index < lines.length) {
        const currentLine = lines[index];
        const itemMatch = currentLine.match(/^[-*]\s+(?:\[([ xX])\]\s*)?(.+)$/);
        const childMatch = currentLine.match(/^\s+[-*]\s+(.+)$/);

        if (itemMatch) {
          currentItem = {
            text: itemMatch[2],
            children: [],
            checked: itemMatch[1]
              ? itemMatch[1].toLowerCase() === "x"
              : undefined,
          };
          items.push(currentItem);
          index += 1;
          continue;
        }

        if (childMatch && currentItem) {
          currentItem.children.push(childMatch[1]);
          index += 1;
          continue;
        }

        break;
      }

      blocks.push({ kind: "unordered-list", items });
      continue;
    }

    const paragraphLines = [trimmed];
    index += 1;
    while (index < lines.length && !isBlockStart(lines, index)) {
      paragraphLines.push(lines[index].trim());
      index += 1;
    }
    blocks.push({ kind: "paragraph", text: paragraphLines.join(" ") });
  }

  return blocks;
};

const renderInline = (text: string): ReactNode[] => {
  const labelMatch = text.match(/^([A-Za-z][A-Za-z0-9 /&()'-]{0,36}:)(.*)$/);
  const tokens = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\{\{[^}]+\}\})/g);

  const content = tokens.map((token, index) => {
    if (/^\*\*[^*]+\*\*$/.test(token)) {
      return <strong key={index}>{token.slice(2, -2)}</strong>;
    }
    if (/^`[^`]+`$/.test(token)) {
      return (
        <code
          key={index}
          className="rounded bg-longhand-ink/[0.07] px-1.5 py-0.5 font-mono text-[0.92em]"
        >
          {token.slice(1, -1)}
        </code>
      );
    }
    if (/^\{\{[^}]+\}\}$/.test(token)) {
      return (
        <span
          key={index}
          className="inline-flex rounded-full border border-longhand-accent/25 bg-longhand-accent-light px-2 py-0.5 font-mono text-[0.82em] font-semibold text-longhand-accent"
        >
          {token}
        </span>
      );
    }
    return token;
  });

  if (!labelMatch || tokens.some((token) => token.startsWith("**"))) {
    return content;
  }

  const [, label, remainder] = labelMatch;
  return [
    <strong key="label">{label}</strong>,
    <span key="remainder">{remainder}</span>,
  ];
};

const MarkdownBlocks = ({ text }: { text: string }) => {
  const blocks = useMemo(() => parseMarkdownBlocks(text), [text]);

  return (
    <div className="min-w-0 max-w-full space-y-4 text-sm leading-relaxed text-longhand-ink/80 sm:text-[0.95rem]">
      {blocks.map((block, blockIndex) => {
        if (block.kind === "heading") {
          const isMajor = block.level <= 2;
          return (
            <h5
              key={blockIndex}
              className={`font-semibold text-longhand-ink ${
                isMajor
                  ? "border-t border-longhand-border pt-5 font-serif text-xl"
                  : "pt-1 text-base"
              }`}
            >
              {renderInline(block.text)}
            </h5>
          );
        }

        if (block.kind === "paragraph") {
          return <p key={blockIndex}>{renderInline(block.text)}</p>;
        }

        if (block.kind === "quote") {
          return (
            <blockquote
              key={blockIndex}
              className="rounded-r-lg border-l-2 border-longhand-accent bg-longhand-accent-light/60 px-4 py-3 text-longhand-ink/75"
            >
              {renderInline(block.text)}
            </blockquote>
          );
        }

        if (block.kind === "code") {
          return (
            <pre
              key={blockIndex}
              className="overflow-x-auto rounded-lg bg-[#0c1524] p-4 font-mono text-xs leading-relaxed text-slate-200 sm:text-sm"
            >
              {block.text}
            </pre>
          );
        }

        if (block.kind === "table") {
          return (
            <div key={blockIndex}>
              <div className="space-y-3 sm:hidden">
                {block.rows.map((row, rowIndex) => (
                  <dl
                    key={rowIndex}
                    className="overflow-hidden rounded-xl border border-longhand-border bg-white"
                  >
                    {block.headers.map((header, cellIndex) => (
                      <div
                        key={cellIndex}
                        className="grid gap-1 border-b border-longhand-border px-3 py-3 last:border-b-0"
                      >
                        <dt className="text-[0.68rem] font-bold uppercase tracking-[0.1em] text-longhand-muted">
                          {renderInline(header)}
                        </dt>
                        <dd className="text-sm text-longhand-ink/80">
                          {renderInline(row[cellIndex] ?? "")}
                        </dd>
                      </div>
                    ))}
                  </dl>
                ))}
              </div>

              <div className="hidden min-w-0 max-w-full overflow-x-auto rounded-xl border border-longhand-border bg-white sm:block">
                <table className="min-w-full border-collapse text-left text-xs sm:text-sm">
                  <thead className="bg-[#f2eee7] text-longhand-ink">
                    <tr>
                      {block.headers.map((header, headerIndex) => (
                        <th
                          key={headerIndex}
                          scope="col"
                          className="whitespace-nowrap border-b border-longhand-border px-3 py-3 font-bold"
                        >
                          {renderInline(header)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-longhand-border">
                    {block.rows.map((row, rowIndex) => (
                      <tr key={rowIndex} className="align-top">
                        {block.headers.map((_, cellIndex) => (
                          <td
                            key={cellIndex}
                            className="min-w-32 px-3 py-3 text-longhand-ink/75"
                          >
                            {renderInline(row[cellIndex] ?? "")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }

        if (block.kind === "ordered-list") {
          return (
            <ol key={blockIndex} className="space-y-3">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex} className="flex items-start gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-longhand-ink text-xs font-bold text-white">
                    {itemIndex + 1}
                  </span>
                  <div className="min-w-0 pt-0.5">
                    <p>{renderInline(item.text)}</p>
                    {item.children.length > 0 ? (
                      <ul className="mt-2 space-y-1.5 border-l border-longhand-border pl-4">
                        {item.children.map((child, childIndex) => (
                          <li key={childIndex}>{renderInline(child)}</li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          );
        }

        return (
          <ul key={blockIndex} className="space-y-2.5">
            {block.items.map((item, itemIndex) => (
              <li key={itemIndex} className="flex items-start gap-2.5">
                {item.checked !== undefined ? (
                  <span
                    className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                      item.checked
                        ? "border-longhand-accent bg-longhand-accent text-white"
                        : "border-longhand-muted/60 bg-white"
                    }`}
                    aria-hidden="true"
                  >
                    {item.checked ? <Check className="h-3 w-3" /> : null}
                  </span>
                ) : (
                  <span
                    className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-longhand-accent"
                    aria-hidden="true"
                  />
                )}
                <div>
                  <span>{renderInline(item.text)}</span>
                  {item.children.length > 0 ? (
                    <ul className="mt-2 space-y-1.5 border-l border-longhand-border pl-4">
                      {item.children.map((child, childIndex) => (
                        <li key={childIndex}>{renderInline(child)}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        );
      })}
    </div>
  );
};

const StepLabel = ({
  number,
  icon: Icon,
  children,
}: {
  number: string;
  icon: typeof Target;
  children: ReactNode;
}) => (
  <div className="mb-4 flex items-center gap-3">
    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-longhand-ink text-xs font-bold text-white">
      {number}
    </span>
    <Icon className="h-4 w-4 text-longhand-accent" aria-hidden="true" />
    <h4 className="text-xs font-bold uppercase tracking-[0.14em] text-longhand-ink">
      {children}
    </h4>
  </div>
);

export const WorkflowInstructionPlaybook = ({
  workflowTitle,
  instructions,
  copied,
  onCopy,
}: WorkflowInstructionPlaybookProps) => {
  const parsed = useMemo(
    () => parseInstruction(instructions),
    [instructions]
  );
  const [outputOpen, setOutputOpen] = useState(
    parsed.kind === "structured" && parsed.output.split("\n").length <= 36
  );

  return (
    <section
      className="w-full min-w-0 max-w-full overflow-hidden rounded-2xl border border-[#253044] bg-[#f8f5ef] shadow-sm"
      aria-label={`Reusable playbook for ${workflowTitle}`}
    >
      <div className="bg-[#0c1524] px-5 py-5 text-white sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#e56522]">
              <BookOpen className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <h4 className="font-serif text-xl font-semibold">
                Reusable playbook
              </h4>
              <p className="mt-0.5 text-sm text-slate-300">
                Copy the instructions, add your details, and check the result.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="w-full border-white/25 bg-white/5 font-bold text-white hover:bg-white/10 hover:text-white sm:w-auto"
            aria-label={`Copy system instructions for ${workflowTitle}`}
            onClick={onCopy}
          >
            {copied ? (
              <>
                <Check className="mr-2 h-4 w-4" aria-hidden="true" />
                Copied
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" aria-hidden="true" />
                Copy full playbook
              </>
            )}
          </Button>
        </div>

        <div
          className="mt-5 grid gap-2 border-t border-white/10 pt-4 text-xs font-semibold text-slate-300 sm:grid-cols-[1fr_auto_1fr_auto_1fr] sm:items-center"
          aria-label="How to use this playbook"
        >
          <span className="flex items-center gap-2">
            <span className="text-[#f07832]">1</span>
            Read the steps
          </span>
          <ArrowRight
            className="hidden h-3.5 w-3.5 text-white/25 sm:block"
            aria-hidden="true"
          />
          <span className="flex items-center gap-2">
            <span className="text-[#f07832]">2</span>
            Replace the placeholders
          </span>
          <ArrowRight
            className="hidden h-3.5 w-3.5 text-white/25 sm:block"
            aria-hidden="true"
          />
          <span className="flex items-center gap-2">
            <span className="text-[#f07832]">3</span>
            Copy, run, and review
          </span>
        </div>
      </div>

      {parsed.kind === "structured" ? (
        <div className="space-y-4 p-4 sm:p-6">
          {parsed.intro ? (
            <div className="flex items-start gap-3 rounded-xl border border-longhand-accent/20 bg-white px-4 py-4">
              <PlayCircle
                className="mt-0.5 h-5 w-5 flex-shrink-0 text-longhand-accent"
                aria-hidden="true"
              />
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-longhand-accent">
                  Start here
                </p>
                <div className="mt-1.5">
                  <MarkdownBlocks text={parsed.intro} />
                </div>
              </div>
            </div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-longhand-border bg-white p-5">
              <StepLabel number="01" icon={Target}>
                Give the AI a clear role
              </StepLabel>
              <MarkdownBlocks text={parsed.role} />
            </div>

            <div className="rounded-xl border border-longhand-border bg-white p-5">
              <StepLabel number="02" icon={Files}>
                Bring your notes and files
              </StepLabel>
              <MarkdownBlocks text={parsed.context} />
              <div className="mt-5 border-t border-longhand-border pt-5">
                <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-longhand-muted">
                  <FileInput className="h-4 w-4" aria-hidden="true" />
                  What you provide
                </div>
                <MarkdownBlocks text={parsed.input} />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-longhand-border bg-white p-5 sm:p-6">
            <StepLabel number="03" icon={ListChecks}>
              Follow the steps
            </StepLabel>
            <MarkdownBlocks text={parsed.steps} />
          </div>

          <details
            className="group overflow-hidden rounded-xl border border-longhand-border bg-white"
            open={outputOpen}
            onToggle={(event) => setOutputOpen(event.currentTarget.open)}
          >
            <summary className="flex cursor-pointer list-none items-center gap-3 px-5 py-5 marker:hidden sm:px-6 [&::-webkit-details-marker]:hidden">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-longhand-ink text-xs font-bold text-white">
                04
              </span>
              <ClipboardCheck
                className="h-4 w-4 flex-shrink-0 text-longhand-accent"
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-bold uppercase tracking-[0.14em] text-longhand-ink">
                  See what the result should include
                </span>
                <span className="mt-1 block text-sm text-longhand-muted">
                  Use this format to check the answer you get.
                </span>
              </span>
              <ChevronDown
                className="h-5 w-5 flex-shrink-0 text-longhand-muted transition-transform group-open:rotate-180"
                aria-hidden="true"
              />
            </summary>
            <div className="border-t border-longhand-border bg-[#fffdf9] px-5 py-5 sm:px-6 sm:py-6">
              <MarkdownBlocks text={parsed.output} />
            </div>
          </details>
        </div>
      ) : (
        <div className="space-y-4 p-4 sm:p-6">
          <div className="rounded-xl border border-longhand-accent/20 bg-white p-5">
            <StepLabel number="01" icon={Target}>
              What this playbook helps you do
            </StepLabel>
            <MarkdownBlocks text={parsed.lead} />
          </div>

          {parsed.body ? (
            <div className="rounded-xl border border-longhand-border bg-white p-5 sm:p-6">
              <StepLabel number="02" icon={ListChecks}>
                Follow the playbook
              </StepLabel>
              <MarkdownBlocks text={parsed.body} />
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
};
